import { useEffect, useMemo, useState } from 'react';
import { phase3API } from '../services/api';
import { SessionData } from '../types';
import Phase3Results from './Phase3Results';
import '../css/AdminSessionPreview.css';
import '../css/Dashboard.css';
import '../css/AnalysisDetail.css';
import '../css/Phase1Input.css';
import '../css/Phase2PDFUpload.css';
import '../css/Phase2FormulaSelection.css';
import '../css/Phase2RealizationSelection.css';

type ViewState =
  | { status: 'idle'; session: null; error: '' }
  | { status: 'loading'; session: null; error: '' }
  | { status: 'loaded'; session: SessionData; error: '' }
  | { status: 'error'; session: null; error: string };

type SessionScreen = 'dashboard' | 'execution';

type PreviewNotice = 'none' | 'pdf' | 'formula' | 'realization' | 'input' | 'download' | 'finish';

type SearchFormulaPreview = {
  id: string;
  formula: string;
  formula_type?: string;
  estimated_count?: number;
  is_approximate?: boolean;
  primary_ipc_description?: string;
  primary_ipc?: string;
};

type RealizationMethodPreview = {
  id: string;
  name: string;
  description: string;
  patent_count: number;
};

const MAX_STEP = 23;

const getInitialSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get('session_id') || '').trim();
};

const normalizeStatus = (raw: unknown) => (raw ?? '').toString().trim().toLowerCase();

const getDisplayTitle = (title: unknown, fallback: string) => {
  const v = (title || '').toString().trim();
  return v || fallback;
};

const tokyoDateFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatCreatedAtLabel = (createdAt: unknown, sessionId: string) => {
  const raw = (createdAt || '').toString().trim();
  const candidates = [raw, sessionId];
  for (const candidate of candidates) {
    const digits = candidate.replace(/\D/g, '');
    if (digits.length >= 8) {
      const y = Number(digits.slice(0, 4));
      const mo = Number(digits.slice(4, 6));
      const d = Number(digits.slice(6, 8));
      const h = digits.length >= 10 ? Number(digits.slice(8, 10)) : 0;
      const mi = digits.length >= 12 ? Number(digits.slice(10, 12)) : 0;
      const sec = digits.length >= 14 ? Number(digits.slice(12, 14)) : 0;
      if (y >= 2000 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
        return tokyoDateFormatter.format(new Date(Date.UTC(y, mo - 1, d, h, mi, sec)));
      }
    }
  }
  return raw || '不明';
};

const formatStatus = (status: unknown) => {
  const s = normalizeStatus(status);
  if (s === 'completed') return '完了';
  if (s === 'excel_ready') return 'Excel生成完了（ダウンロード待ち）';
  if (s === 'judging_patent_quality') return '重要度を評価中';
  if (s === 'generating_excel') return 'Excel作成中';
  if (s === 'analyzing_pdfs') return 'PDF解析中';
  if (s === 'generating_formulas') return '検索式を生成中';
  if (s === 'executing_final_search') return '特許情報を収集中';
  if (s === 'extracting_realization_methods') return '技術項目を抽出中';
  if (s === 'extracting_components') return '技術要素を整理中';
  if (s === 'in_progress') return '進行中';
  return status ? String(status) : '不明';
};

const phaseFromStep = (step: number) => {
  if (step >= 19) return 'detail';
  if (step >= 15) return 'realization_selection';
  if (step >= 8) return 'formula_selection';
  if (step >= 4) return 'pdf_upload';
  return 'input';
};

const workflowStep = (phase: string) => {
  if (phase === 'input' || phase === 'pdf_upload') return 1;
  if (phase === 'formula_selection') return 2;
  if (phase === 'realization_selection') return 3;
  return 4;
};

const stepStatus = (current: number, step: number) => {
  if (current === step) return 'active';
  if (current > step) return 'completed';
  return 'pending';
};

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const getString = (value: unknown, fallback = '') => {
  if (typeof value === 'string') return value;
  if (value == null) return fallback;
  return String(value);
};

const getNumber = (value: unknown, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const safeArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const toSessionRecord = (session: SessionData): Record<string, unknown> => ({ ...session });

const splitUserInput = (input: unknown) => {
  const text = getString(input);
  const parts = text
    .split(/\n{2,}|(?=技術的特徴[:：])|(?=課題[:：])|(?=効果作用[:：])/)
    .map((part) => part.replace(/^(利用分野|技術的特徴|課題|効果作用)[:：]\s*/, '').trim())
    .filter(Boolean);

  return {
    usage: parts[0] || text,
    technical: parts[1] || '',
    problem: parts[2] || '',
    effect: parts[3] || '',
  };
};

const formulasFromSession = (session: SessionData): SearchFormulaPreview[] =>
  safeArray(session.search_formulas).map((item, index) => {
    const record = toRecord(item);
    const id = getString(record.id, `formula-${index + 1}`);
    return {
      id,
      formula: getString(record.formula, '検索式データがありません'),
      formula_type: getString(record.formula_type),
      estimated_count: getNumber(record.estimated_count, -1),
      is_approximate: Boolean(record.is_approximate),
      primary_ipc_description: getString(record.primary_ipc_description),
      primary_ipc: getString(record.primary_ipc),
    };
  });

const methodsFromSession = (session: SessionData): RealizationMethodPreview[] =>
  safeArray(session.realization_methods).map((item, index) => {
    const record = toRecord(item);
    return {
      id: getString(record.id, `method-${index + 1}`),
      name: getString(record.name, `技術項目 ${index + 1}`),
      description: getString(record.description),
      patent_count: getNumber(record.patent_count, 0),
    };
  });

const noticeText = (notice: PreviewNotice) => {
  switch (notice) {
    case 'pdf':
      return 'プレビューのため、PDFの選択・アップロード・解析開始は実行されません。';
    case 'formula':
      return 'プレビューのため、検索式の選択状態だけを画面内で変更します。特許収集は実行されません。';
    case 'realization':
      return 'プレビューのため、技術項目の選択状態だけを画面内で変更します。特許マップ作成は実行されません。';
    case 'input':
      return 'プレビューのため、入力内容の変更や次ステップへの保存は実行されません。';
    case 'download':
      return 'プレビューのため、Excelファイルのダウンロードは実行されません。';
    case 'finish':
      return 'プレビューのため、完了状態への更新は実行されません。';
    default:
      return '';
  }
};

/**
 * Admin-only read-only page that previews one session using customer-facing UI.
 * Interactive controls are local-only facsimiles and never mutate the session.
 */
const AdminSessionView = () => {
  const [sessionId, setSessionId] = useState(getInitialSessionId);
  const [screen, setScreen] = useState<SessionScreen>('dashboard');
  const [notice, setNotice] = useState<PreviewNotice>('none');
  const [selectedFormulaIds, setSelectedFormulaIds] = useState<string[]>([]);
  const [selectedMethodIds, setSelectedMethodIds] = useState<string[]>([]);
  const [state, setState] = useState<ViewState>({
    status: 'idle',
    session: null,
    error: '',
  });

  const normalizedSessionId = sessionId.trim();

  const loadSession = async () => {
    if (!normalizedSessionId) {
      setState({
        status: 'error',
        session: null,
        error: 'セッションIDを入力してください。',
      });
      return;
    }

    setState({ status: 'loading', session: null, error: '' });
    setNotice('none');
    const params = new URLSearchParams(window.location.search);
    params.set('session_id', normalizedSessionId);
    window.history.replaceState(null, '', `/admin/session-view?${params.toString()}`);

    try {
      const session = await phase3API.getAdminSessionPreview(normalizedSessionId);
      setState({ status: 'loaded', session, error: '' });
      setScreen('dashboard');
      setSelectedFormulaIds(
        safeArray(session.selected_formula_ids)
          .map((id) => getString(id))
          .filter(Boolean)
      );
      setSelectedMethodIds(
        safeArray(session.selected_realization_ids)
          .map((id) => getString(id))
          .filter(Boolean)
      );
    } catch (error) {
      setState({
        status: 'error',
        session: null,
        error:
          error instanceof Error && error.message
            ? error.message
            : 'セッション情報の取得に失敗しました。',
      });
    }
  };

  useEffect(() => {
    if (normalizedSessionId) {
      void loadSession();
    }
    // Run only on initial mount. Manual reload is handled by the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = useMemo(() => {
    if (state.status !== 'loaded') return null;
    const session = state.session;
    const sessionRecord = toSessionRecord(session);
    const step = Math.max(0, Number(session.current_step || 0));
    const pct = Math.min(100, Math.floor((Math.min(MAX_STEP, step) / MAX_STEP) * 100));
    const status = normalizeStatus(session.status);
    const isResultReady = step >= 19 || status === 'completed' || status === 'excel_ready';
    const phase = phaseFromStep(step);
    const currentWorkflowStep = workflowStep(phase);
    const title = getDisplayTitle(
      session.title,
      `セッション ${session.session_id || normalizedSessionId}`
    );
    const createdAt = formatCreatedAtLabel(
      sessionRecord.created_at,
      session.session_id || normalizedSessionId
    );
    const formulas = formulasFromSession(session);
    const methods = methodsFromSession(session);
    const progressMessage = getString(sessionRecord.progress_message).trim();
    return {
      session,
      sessionRecord,
      step,
      pct,
      isResultReady,
      phase,
      currentWorkflowStep,
      title,
      createdAt,
      formulas,
      methods,
      progressMessage,
    };
  }, [normalizedSessionId, state]);

  const renderPreviewNotice = () => {
    const text = noticeText(notice);
    if (!text) return null;
    return (
      <div className="admin-session-local-notice" role="status">
        {text}
      </div>
    );
  };

  const renderDashboardCard = () => {
    if (!preview) return null;
    return (
      <section className="admin-preview-content admin-session-dashboard-preview">
        <div className="history-card">
          <div className="history-card-left">
            <div className={`status-icon ${preview.isResultReady ? 'completed' : 'processing'}`}>
              {preview.isResultReady ? '✓' : '⟳'}
            </div>
            <div className="history-info">
              <h4 className="history-title">{preview.title}</h4>
              <div className="history-meta">
                <span>セッションID: {preview.session.session_id || normalizedSessionId}</span>
              </div>
              <div className="history-meta">
                <span>進捗: {formatStatus(preview.session.status)}</span>
              </div>
              <div className="history-meta">
                <span>作成開始日時: {preview.createdAt}</span>
              </div>
              <div className="active-progress">
                <div className="active-progress-bar">
                  <div className="active-progress-fill" style={{ width: `${preview.pct}%` }} />
                </div>
                <div className="active-progress-pct">{preview.pct}%</div>
              </div>
            </div>
          </div>
          <div className="history-card-right">
            {preview.progressMessage && !preview.isResultReady ? (
              <div className="admin-session-progress-message" aria-live="polite">
                <span className="admin-session-progress-dot" aria-hidden="true" />
                <span>{preview.progressMessage}</span>
              </div>
            ) : null}
            <button className="view-btn" type="button" onClick={() => setScreen('execution')}>
              実行画面へ
            </button>
          </div>
        </div>
      </section>
    );
  };

  const renderWorkflowHeader = () => {
    if (!preview) return null;
    return (
      <div className="container admin-session-execution-shell">
        <div
          className={`workflow-header${preview.phase === 'detail' ? ' workflow-header-detail' : ''}`}
        >
          <h1>特許マップ作成 進行状況</h1>
          <div className="phase-indicator">
            <div className={`phase-step ${stepStatus(preview.currentWorkflowStep, 1)}`}>
              <div className="phase-step-top">
                <span className="phase-step-num">Step 1</span>
                <span className="phase-step-title">分野の指定</span>
              </div>
              <ul className="phase-step-list">
                <li className={preview.currentWorkflowStep >= 1 ? 'completed' : ''}>概要を入力</li>
                <li className={preview.currentWorkflowStep >= 1 ? 'completed' : ''}>
                  特許例を入力
                </li>
              </ul>
            </div>
            <div className={`phase-step ${stepStatus(preview.currentWorkflowStep, 2)}`}>
              <div className="phase-step-top">
                <span className="phase-step-num">Step 2</span>
                <span className="phase-step-title">特許情報の収集</span>
              </div>
              <ul className="phase-step-list">
                <li className={preview.currentWorkflowStep >= 2 ? 'completed' : ''}>
                  特許検索式を選択
                </li>
                <li className={preview.currentWorkflowStep >= 2 ? 'completed' : ''}>
                  特許を自動収集。技術項目を抽出
                </li>
              </ul>
            </div>
            <div className={`phase-step ${stepStatus(preview.currentWorkflowStep, 3)}`}>
              <div className="phase-step-top">
                <span className="phase-step-num">Step 3</span>
                <span className="phase-step-title">特許マップ作成</span>
              </div>
              <ul className="phase-step-list">
                <li className={preview.currentWorkflowStep >= 3 ? 'completed' : ''}>
                  特許マップに掲載する対象技術を選択
                </li>
                <li className={preview.currentWorkflowStep >= 3 ? 'completed' : ''}>
                  特許マップ自動生成
                </li>
              </ul>
            </div>
            <div className={`phase-step ${stepStatus(preview.currentWorkflowStep, 4)}`}>
              <div className="phase-step-top">
                <span className="phase-step-num">Step 4</span>
                <span className="phase-step-title">特許マップ ダウンロード</span>
              </div>
              <ul className="phase-step-list">
                <li className={preview.currentWorkflowStep >= 4 ? 'completed' : ''}>
                  ダウンロード
                </li>
                <li className={preview.phase === 'detail' ? 'completed' : ''}>完了</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInputPreview = () => {
    if (!preview) return null;
    const input = splitUserInput(preview.session.user_input);
    const totalLength = [input.usage, input.technical, input.problem, input.effect].join('').length;

    return (
      <div className="phase1-input admin-session-execution-content">
        <div className="phase-card">
          <h2 className="phase-title">
            <span className="phase-title-step">Step1:</span>
            <span className="phase-title-text">分野の指定</span>
          </h2>
          <div className="workflow-step-description phase-description-banner">
            特許マップの分野を指定するため、概要（利用分野、技術的特徴、課題、効果作用）を完了してください
          </div>
          {[
            ['usage-field', '1. 利用分野（Application Field）', input.usage],
            ['technical-feature', '2. 技術的特徴（Technical Features）', input.technical],
            ['problem', '3. 課題（Problem/Challenge）', input.problem],
            ['effect', '4. 効果作用（Effect/Impact）', input.effect],
          ].map(([id, label, value]) => (
            <div className="input-section" key={id}>
              <label htmlFor={`preview-${id}`}>
                <span className="label-text">{label}</span>
                <span className="required">*</span>
              </label>
              <textarea
                id={`preview-${id}`}
                value={value}
                onChange={() => setNotice('input')}
                rows={3}
              />
            </div>
          ))}
          <div className="char-counter-total">
            <span className="char-counter">合計: {totalLength} /2000文字</span>
          </div>
          <div className="submit-btn-wrapper">
            <button type="button" className="submit-btn" onClick={() => setNotice('input')}>
              次へ
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPdfUploadPreview = () => (
    <div className="phase2-pdf-upload admin-session-execution-content">
      <div className="phase-card">
        <h2 className="phase-title">
          <span className="phase-title-step">Step1:</span>
          <span className="phase-title-text">分野の指定</span>
        </h2>
        <div className="workflow-step-description phase-description-banner">
          特許マップの分野を指定するため、特許例入力（PDF形式）を完了してください
        </div>

        <div
          className="dropzone"
          onClick={() => setNotice('pdf')}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            setNotice('pdf');
          }}
        >
          <div className="dropzone-content">
            <div className="upload-icon">📄</div>
            <p className="dropzone-text">PDFファイルをここにドラッグ&ドロップ</p>
            <p className="dropzone-or">または</p>
            <button type="button" className="file-select-btn" onClick={() => setNotice('pdf')}>
              ファイルを選択
            </button>
          </div>
        </div>

        <div className="upload-btn-wrapper">
          <button type="button" className="upload-btn btn-primary" onClick={() => setNotice('pdf')}>
            アップロードして解析開始
          </button>
        </div>
      </div>
    </div>
  );

  const formulaTypeLabel = (formula: SearchFormulaPreview) => {
    if (formula.primary_ipc_description) return formula.primary_ipc_description;
    if (formula.primary_ipc) return formula.primary_ipc;
    return formula.formula_type || '検索式';
  };

  const toggleFormula = (id: string) => {
    setSelectedFormulaIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    );
  };

  const renderFormulaSelectionPreview = () => {
    if (!preview) return null;
    const formulas = preview.formulas;

    return (
      <div className="phase2-formula-selection admin-session-execution-content">
        <div className="phase-card">
          <h2 className="phase-title">
            <span className="phase-title-step">Step2:</span>
            <span className="phase-title-text">特許情報の収集</span>
          </h2>
          <div className="workflow-step-description phase-description-banner">
            特許マップに掲載する対象の特許を収集（取得）するため、適した検索式（収集式）を選択してください
          </div>

          <div className="formula-selection-actions">
            <div className="selection-actions">
              <button
                type="button"
                className="select-all-btn"
                onClick={() => {
                  setSelectedFormulaIds(formulas.map((formula) => formula.id));
                  setNotice('formula');
                }}
              >
                検索式案 {formulas.length}件全て選択
              </button>
              <button
                type="button"
                className="clear-all-btn"
                onClick={() => {
                  setSelectedFormulaIds([]);
                  setNotice('formula');
                }}
              >
                選択解除
              </button>
            </div>
          </div>

          <div className="formulas-list">
            {formulas.length === 0 && (
              <div className="empty-state" style={{ padding: 16 }}>
                <p>検索式が0件のため表示できません。</p>
              </div>
            )}
            {formulas.map((formula, index) => (
              <div
                key={formula.id}
                className={`formula-item ${selectedFormulaIds.includes(formula.id) ? 'selected' : ''}`}
                onClick={() => {
                  toggleFormula(formula.id);
                  setNotice('formula');
                }}
              >
                <div className="formula-header">
                  <div className="formula-heading">
                    <input
                      type="checkbox"
                      checked={selectedFormulaIds.includes(formula.id)}
                      onChange={() => {
                        toggleFormula(formula.id);
                        setNotice('formula');
                      }}
                      onClick={(event) => event.stopPropagation()}
                      className="formula-checkbox"
                    />
                    <div className="formula-heading-text">
                      <div className="formula-name">検索式 案{index + 1}</div>
                      <div className="formula-text">{formula.formula}</div>
                    </div>
                  </div>
                </div>
                <div className="formula-footer">
                  <span className="formula-meta-chip formula-meta-primary">
                    {formulaTypeLabel(formula)}
                  </span>
                  <span className="estimated-count">
                    ヒット見込み:{' '}
                    {formula.estimated_count != null && formula.estimated_count >= 0
                      ? `${formula.is_approximate ? '約' : ''}${formula.estimated_count.toLocaleString()}件`
                      : '不明'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="formula-actions-bottom">
            <button
              type="button"
              onClick={() => setNotice('formula')}
              className="btn-primary formula-submit-btn"
            >
              選択した {selectedFormulaIds.length}件の検索式で実行
            </button>
          </div>
        </div>
      </div>
    );
  };

  const toggleMethod = (id: string) => {
    setSelectedMethodIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    );
  };

  const renderRealizationSelectionPreview = () => {
    if (!preview) return null;
    const methods = preview.methods;

    return (
      <div className="phase2-realization-selection admin-session-execution-content">
        <div className="phase-card">
          <h2 className="phase-title">
            <span className="phase-title-step">Step3:</span>
            <span className="phase-title-text">特許マップ作成</span>
          </h2>
          <div className="workflow-step-description phase-description-banner">
            特許マップに掲載する技術項目を選定してください
          </div>

          <div className="selection-summary">
            <span className="summary-text">
              {methods.length}件中 {selectedMethodIds.length}件選択
            </span>
            <button
              type="button"
              onClick={() => {
                setSelectedMethodIds(methods.map((method) => method.id));
                setNotice('realization');
              }}
              className="select-all-btn"
            >
              全て選択
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedMethodIds([]);
                setNotice('realization');
              }}
              className="clear-all-btn"
            >
              選択解除
            </button>
          </div>

          <div className="methods-grid">
            {methods.length === 0 && (
              <div className="empty-state" style={{ padding: 16 }}>
                <p>技術項目が0件のため表示できません。</p>
              </div>
            )}
            {methods.map((method) => (
              <div
                key={method.id}
                className={`method-card ${selectedMethodIds.includes(method.id) ? 'selected' : ''}`}
                onClick={() => {
                  toggleMethod(method.id);
                  setNotice('realization');
                }}
              >
                <div className="method-header">
                  <input
                    type="checkbox"
                    checked={selectedMethodIds.includes(method.id)}
                    onChange={() => {
                      toggleMethod(method.id);
                      setNotice('realization');
                    }}
                    onClick={(event) => event.stopPropagation()}
                    className="method-checkbox"
                  />
                  <span className="patent-count">{method.patent_count}件</span>
                </div>
                <h3 className="method-name">{method.name}</h3>
                <p className="method-description">{method.description}</p>
              </div>
            ))}
          </div>

          <div className="nav-actions">
            <button
              type="button"
              onClick={() => setNotice('realization')}
              className="nav-btn nav-btn-primary"
            >
              特許マップの作成開始（選択した項目を軸とした構成の特許マップを作成)
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResultPreview = () => {
    if (!preview) return null;
    return (
      <section className="admin-session-result-preview">
        <div className="analysis-detail-container">
          <div className="analysis-detail-content">
            <div className="tab-content">
              <Phase3Results
                sessionId={preview.session.session_id || normalizedSessionId}
                workflowData={{}}
                onRestart={() => undefined}
                initialSessionData={preview.sessionRecord}
                readOnlyPreview
              />
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderExecutionScreen = () => {
    if (!preview) return null;

    return (
      <>
        <section className="admin-session-toolbar">
          <button
            type="button"
            className="admin-preview-home"
            onClick={() => setScreen('dashboard')}
          >
            戻る
          </button>
        </section>
        {renderWorkflowHeader()}
        {renderPreviewNotice()}
        {preview.isResultReady
          ? renderResultPreview()
          : preview.phase === 'input'
            ? renderInputPreview()
            : preview.phase === 'pdf_upload'
              ? renderPdfUploadPreview()
              : preview.phase === 'formula_selection'
                ? renderFormulaSelectionPreview()
                : renderRealizationSelectionPreview()}
      </>
    );
  };

  return (
    <main className="admin-preview-page admin-session-view-page">
      <section className="admin-preview-hero">
        <div>
          <p className="admin-preview-eyebrow">Admin customer-view preview</p>
          <h1>顧客画面プレビュー</h1>
          <p>
            指定した 1 セッションだけを、顧客画面と同じ流れで read-only 表示します。
            操作しても画面内の表示だけが変わり、ジョブ再開・再実行・ダウンロード状態更新は行いません。
          </p>
        </div>
        <a href="/" className="admin-preview-home">
          管理画面へ戻る
        </a>
      </section>

      <section className="admin-preview-search">
        <label htmlFor="admin-session-view-session-id">セッションID</label>
        <div className="admin-preview-search-row">
          <input
            id="admin-session-view-session-id"
            value={sessionId}
            onChange={(event) => setSessionId(event.target.value)}
            placeholder="SESSION-YYYYMMDD-..."
          />
          <button type="button" onClick={loadSession} disabled={state.status === 'loading'}>
            {state.status === 'loading' ? '読み込み中' : '表示'}
          </button>
        </div>
      </section>

      {state.status === 'error' && <div className="admin-preview-error">{state.error}</div>}

      {preview && (screen === 'dashboard' ? renderDashboardCard() : renderExecutionScreen())}
    </main>
  );
};

export default AdminSessionView;
